import type {
  AgingRow,
  CreditNoteListItem,
  Customer,
  CustomerAccountSummary,
  CustomerGroup,
  CustomerGroupInput,
  CustomerGroupUpdate,
  CustomerInput,
  CustomerListItem,
  CustomerOption,
  CustomerRef,
  CustomerSummary,
  CustomerUpdate,
  CustomPrice,
  Page,
  SalesInvoiceListItem,
} from '@sahab/contracts'
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type DownloadedFile } from '../../lib/api'
import type { Language } from '../../lib/format'
import { pricingKeys } from '../catalog/pricing'
import { invalidateSales, type ReceiptListItem, salesKeys } from '../sales/queries'
import { CUSTOMER_BALANCE_FILTERS, type CustomerBalanceView, type CustomerListParams } from './search'

export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  list: (query: CustomerListParams) => [...customerKeys.lists(), query] as const,
  summary: () => [...customerKeys.all, 'summary'] as const,
  detail: (id: string) => [...customerKeys.all, 'detail', id] as const,
}

/**
 * The profile's figures move with every sales document, so they live under the sales keys: any
 * posting, allocation or refund (`invalidateSales`) refreshes them with the lists.
 */
export const customerAccountKeys = {
  summary: (id: string) => [...salesKeys.all, 'customer-summary', id] as const,
  aging: (id: string, asOf: string) => [...salesKeys.all, 'aging', asOf, id] as const,
  recent: (id: string) => [...salesKeys.all, 'customer-recent', id] as const,
  /** List counts per balance view (Owes us, Overdue, In credit). */
  balanceCounts: () => [...salesKeys.all, 'customer-balance-counts'] as const,
}

export const groupKeys = {
  all: ['customer-groups'] as const,
}

function listQuery(query: CustomerListParams) {
  return {
    sort: query.sort,
    q: query.q,
    view: query.view,
    groupId: query.groupId,
    balance: query.balance,
  }
}

export function customerListQueryOptions(query: CustomerListParams) {
  return queryOptions({
    queryKey: customerKeys.list(query),
    queryFn: ({ signal }) =>
      api.get<Page<CustomerListItem>>('/customers', {
        signal,
        query: { page: query.page, pageSize: query.pageSize, ...listQuery(query) },
      }),
    // Keep the previous page on screen while the next one loads (no flash to skeletons).
    placeholderData: keepPreviousData,
  })
}

/**
 * `GET /customers/export`: every customer matching the list's filters and sort (not only this page),
 * as a CSV file with headers in the UI language. Balance columns come only with sales.read.
 */
export function exportCustomers(query: CustomerListParams, language: Language): Promise<DownloadedFile> {
  return api.download('/customers/export', {
    query: { format: 'csv', lang: language, ...listQuery(query) },
    fallbackName: 'customers.csv',
  })
}

export const customerSummaryQueryOptions = queryOptions({
  queryKey: customerKeys.summary(),
  queryFn: ({ signal }) => api.get<CustomerSummary>('/customers/summary', { signal }),
})

/**
 * How many current customers each balance view holds, for the view tabs (sales.read only). The
 * list endpoint's `total` for one-row pages: the summary endpoint does not count them.
 */
export const customerBalanceCountsQueryOptions = queryOptions({
  queryKey: customerAccountKeys.balanceCounts(),
  queryFn: async ({ signal }): Promise<Record<CustomerBalanceView, number>> => {
    const totals = await Promise.all(
      CUSTOMER_BALANCE_FILTERS.map((balance) =>
        api
          .get<Page<CustomerListItem>>('/customers', { signal, query: { balance, pageSize: 1 } })
          .then((page) => [balance, page.total] as const),
      ),
    )
    return Object.fromEntries(totals) as Record<CustomerBalanceView, number>
  },
  staleTime: 30_000,
})

export function customerQueryOptions(id: string) {
  return queryOptions({
    queryKey: customerKeys.detail(id),
    queryFn: ({ signal }) => api.get<Customer>(`/customers/${encodeURIComponent(id)}`, { signal }),
  })
}

/** The profile's key figures, tab counts and attention counts in one call (sales.read). */
export function customerAccountSummaryQueryOptions(id: string) {
  return queryOptions({
    queryKey: customerAccountKeys.summary(id),
    queryFn: ({ signal }) =>
      api.get<CustomerAccountSummary>(`/customers/${encodeURIComponent(id)}/summary`, { signal }),
  })
}

/** The customer's row of the receivables aging as of `asOf` (null: nothing owed and no credit). */
export function customerAgingQueryOptions(id: string, asOf: string) {
  return queryOptions({
    queryKey: customerAccountKeys.aging(id, asOf),
    queryFn: async ({ signal }): Promise<AgingRow | null> => {
      const rows = await api.get<AgingRow[]>('/sales/aging', { signal, query: { asOf, customerId: id } })
      return rows.find((row) => row.customer.id === id) ?? null
    },
  })
}

export type RecentDocument =
  | { kind: 'invoice'; date: string; doc: SalesInvoiceListItem }
  | { kind: 'receipt'; date: string; doc: ReceiptListItem }
  | { kind: 'creditNote'; date: string; doc: CreditNoteListItem }

/** How many recent documents the Overview lists. */
export const RECENT_DOCUMENTS = 6

/** The customer's newest invoices, receipts and credit notes, merged newest first (Overview). */
export function customerRecentDocumentsQueryOptions(id: string) {
  return queryOptions({
    queryKey: customerAccountKeys.recent(id),
    queryFn: async ({ signal }): Promise<RecentDocument[]> => {
      const page = { customerId: id, pageSize: RECENT_DOCUMENTS }
      const [invoices, receipts, notes] = await Promise.all([
        api.get<Page<SalesInvoiceListItem>>('/sales/invoices', {
          signal,
          query: { ...page, sort: '-invoiceDate' },
        }),
        api.get<Page<ReceiptListItem>>('/sales/receipts', {
          signal,
          query: { ...page, sort: '-receiptDate' },
        }),
        api.get<Page<CreditNoteListItem>>('/sales/credit-notes', {
          signal,
          query: { ...page, sort: '-noteDate' },
        }),
      ])
      const all: RecentDocument[] = [
        ...invoices.items.map((doc) => ({ kind: 'invoice' as const, date: doc.invoiceDate, doc })),
        ...receipts.items.map((doc) => ({ kind: 'receipt' as const, date: doc.receiptDate, doc })),
        ...notes.items.map((doc) => ({ kind: 'creditNote' as const, date: doc.noteDate, doc })),
      ]
      return all.sort((a, b) => b.date.localeCompare(a.date)).slice(0, RECENT_DOCUMENTS)
    },
  })
}

/**
 * The customer a form was opened with (`?customerId=`), through the picker endpoint: document
 * creators may call it without customers.read. Null when the id matches nothing.
 */
export function customerPresetQueryOptions(id: string) {
  return queryOptions({
    queryKey: [...customerKeys.lists(), 'preset', id] as const,
    queryFn: async ({ signal }): Promise<CustomerRef | null> => {
      const [found] = await api.get<CustomerOption[]>('/customers/options', { signal, query: { id } })
      return found ? { id: found.id, code: found.code, name: found.name, nameAr: found.nameAr } : null
    },
  })
}

export const customerGroupsQueryOptions = queryOptions({
  queryKey: groupKeys.all,
  queryFn: ({ signal }) => api.get<CustomerGroup[]>('/customer-groups', { signal }),
  staleTime: 5 * 60_000,
})

/** How many of a customer's custom prices the profile lists. */
export const CUSTOM_PRICES_SHOWN = 50

/**
 * `GET /custom-prices?customerId=…&current=true` (products.read): the customer's own custom prices still
 * in force or to come (ADR-045), by product. Edited on the product page; under the `custom-prices` key,
 * so whatever refreshes custom prices refreshes these too.
 */
export function customerCustomPricesQueryOptions(customerId: string) {
  return queryOptions({
    queryKey: ['custom-prices', 'customer', customerId] as const,
    queryFn: ({ signal }) =>
      api.get<Page<CustomPrice>>('/custom-prices', {
        signal,
        query: { customerId, current: true, pageSize: CUSTOM_PRICES_SHOWN },
      }),
  })
}

/**
 * Lists, counts and group member counts all depend on customers: refresh them together. So does the
 * level documents price a customer at (their own, or their groups').
 */
function useInvalidateCustomerViews() {
  const queryClient = useQueryClient()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: customerKeys.summary() }),
      queryClient.invalidateQueries({ queryKey: groupKeys.all }),
      queryClient.invalidateQueries({ queryKey: pricingKeys.all }),
    ])
}

export function useCreateCustomer() {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateCustomerViews()
  return useMutation({
    mutationFn: (input: CustomerInput) => api.post<Customer>('/customers', input),
    onSuccess: (customer) => {
      queryClient.setQueryData(customerKeys.detail(customer.id), customer)
      void invalidate()
    },
  })
}

export function useUpdateCustomer(id: string) {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateCustomerViews()
  return useMutation({
    mutationFn: (input: CustomerUpdate) => api.put<Customer>(`/customers/${encodeURIComponent(id)}`, input),
    onSuccess: (customer) => {
      queryClient.setQueryData(customerKeys.detail(customer.id), customer)
      void invalidate()
    },
  })
}

/** Archive or restore any customer (the list's row actions and the profile's More menu). */
export function useSetCustomerArchived() {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateCustomerViews()
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api.post<void>(`/customers/${encodeURIComponent(id)}/${archived ? 'archive' : 'restore'}`),
    onSuccess: (_, { id }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: customerKeys.detail(id) }),
        invalidate(),
        // The balance views count current customers only.
        invalidateSales(queryClient),
      ]),
  })
}

/** A new group, or an edit at the version the drawer opened with (409 VERSION_CONFLICT when stale). */
export function useSaveCustomerGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (
      save: { id: string; input: CustomerGroupUpdate } | { id: null; input: CustomerGroupInput },
    ) =>
      save.id
        ? api.put<CustomerGroup>(`/customer-groups/${encodeURIComponent(save.id)}`, save.input)
        : api.post<CustomerGroup>('/customer-groups', save.input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: groupKeys.all }),
        // Group names appear on customer rows and records, and a group's level is its members'.
        queryClient.invalidateQueries({ queryKey: customerKeys.all }),
        queryClient.invalidateQueries({ queryKey: pricingKeys.all }),
      ]),
  })
}

export function useDeleteCustomerGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/customer-groups/${encodeURIComponent(id)}`),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: groupKeys.all }),
        queryClient.invalidateQueries({ queryKey: customerKeys.all }),
        queryClient.invalidateQueries({ queryKey: pricingKeys.all }),
      ]),
  })
}
